import { useState, useEffect } from "react";
import { useNavigate, Link } from "react-router-dom";
import {
  Bell,
  AlertCircle,
  CheckCircle,
  Calendar,
  FileText,
  ChevronRight,
} from "lucide-react";
import {
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from "./ui/tabs";
import { Badge } from "./ui/badge";

interface Announcement {
  id: number;
  project_no: string;
  project_name: string;
  publish_time: string;
  budget?: string;
}

interface AnnouncementsData {
  bidding: Announcement[];
  changes: Announcement[];
  results: Announcement[];
  others: Announcement[];
}

export function AnnouncementSection() {
  const navigate = useNavigate();
  const [data, setData] = useState<AnnouncementsData>({
    bidding: [],
    changes: [],
    results: [],
    others: []
  });
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchAnnouncements();
  }, []);

  const fetchAnnouncements = async () => {
    try {
      const resp = await fetch('/api/public/announcements/home');
      const result = await resp.json();
      if (result.ok && result.data) {
        setData(result.data);
      }
    } catch (err) {
      console.error('获取公告列表失败:', err);
    } finally {
      setLoading(false);
    }
  };

  // 格式化日期
  const formatDate = (dateStr: string) => {
    if (!dateStr) return '';
    const date = new Date(dateStr);
    return date.toLocaleDateString('zh-CN', {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit'
    }).replace(/\//g, '-');
  };

  // 点击公告跳转到详情页
  const handleClick = (announcement: Announcement, type: number) => {
    navigate(`/announcement-detail/${type}/${announcement.id}`);
  };

  const renderAnnouncementItem = (item: Announcement, type: number) => (
    <div 
      className="group cursor-pointer"
      onClick={() => handleClick(item, type)}
    >
      <div className="bg-white border border-gray-200 rounded-lg p-5 hover:shadow-md transition-all hover:border-[#003c8c]/30">
        <div className="flex items-start justify-between space-x-4">
          <div className="flex-1 min-w-0">
            <span className="text-gray-700 hover:text-[#003c8c] transition-colors line-clamp-2 mb-3 block">
              {item.project_name}
            </span>
            <div className="flex items-center flex-wrap gap-4 text-sm">
              <div className="flex items-center space-x-2 text-gray-500">
                <Calendar className="w-4 h-4 text-[#003c8c]" />
                <span>{formatDate(item.publish_time)}</span>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );

  const renderEmptyState = () => (
    <div className="text-center py-12 text-gray-400">
      <FileText className="w-12 h-12 mx-auto mb-3 opacity-50" />
      <p>暂无公告</p>
    </div>
  );

  return (
    <div className="bg-white border border-gray-200 rounded-xl p-8 shadow-lg">
      {/* Title section */}
      <div className="flex items-center justify-between mb-8">
        <div className="flex items-center space-x-4">
          <div className="w-1 h-8 bg-[#003c8c] rounded-full" />
          <h2 className="text-2xl text-gray-800">招采公告</h2>
        </div>
        <Link
          to="/trade-announcements"
          className="text-[#003c8c] hover:text-[#0050b8] flex items-center text-sm"
        >
          查看更多
          <ChevronRight className="w-4 h-4 ml-1" />
        </Link>
      </div>

      <Tabs defaultValue="bidding" className="w-full">
        <TabsList className="grid w-full grid-cols-4 mb-8 h-14 md:h-14 h-12 bg-gray-100">
          <TabsTrigger
            value="bidding"
            className="cursor-pointer flex items-center justify-center space-x-1 md:space-x-2 h-full data-[state=active]:bg-gradient-to-r data-[state=active]:from-[#003c8c] data-[state=active]:to-[#0050b8] data-[state=active]:text-white"
          >
            <Bell className="w-4 h-4 hidden md:block" />
            <span className="md:hidden">招采</span>
            <span className="hidden md:inline">招标采购公告</span>
            <Badge variant="secondary" className="ml-1 md:ml-2 hidden md:inline-flex">
              {data.bidding.length}
            </Badge>
          </TabsTrigger>
          <TabsTrigger
            value="changes"
            className="cursor-pointer flex items-center justify-center space-x-1 md:space-x-2 h-full data-[state=active]:bg-gradient-to-r data-[state=active]:from-[#a51c52] data-[state=active]:to-[#c92a60] data-[state=active]:text-white"
          >
            <AlertCircle className="w-4 h-4 hidden md:block" />
            <span className="md:hidden">变更</span>
            <span className="hidden md:inline">变更公告</span>
            <Badge variant="secondary" className="ml-1 md:ml-2 hidden md:inline-flex">
              {data.changes.length}
            </Badge>
          </TabsTrigger>
          <TabsTrigger
            value="results"
            className="cursor-pointer flex items-center justify-center space-x-1 md:space-x-2 h-full data-[state=active]:bg-gradient-to-r data-[state=active]:from-[#0050b8] data-[state=active]:to-[#0088ff] data-[state=active]:text-white"
          >
            <CheckCircle className="w-4 h-4 hidden md:block" />
            <span className="md:hidden">中标</span>
            <span className="hidden md:inline">中标成交公告</span>
            <Badge variant="secondary" className="ml-1 md:ml-2 hidden md:inline-flex">
              {data.results.length}
            </Badge>
          </TabsTrigger>
          <TabsTrigger
            value="others"
            className="cursor-pointer flex items-center justify-center space-x-1 md:space-x-2 h-full data-[state=active]:bg-gradient-to-r data-[state=active]:from-[#666666] data-[state=active]:to-[#888888] data-[state=active]:text-white"
          >
            <FileText className="w-4 h-4 hidden md:block" />
            <span className="md:hidden">其他</span>
            <span className="hidden md:inline">其他公告</span>
            <Badge variant="secondary" className="ml-1 md:ml-2 hidden md:inline-flex">
              {data.others.length}
            </Badge>
          </TabsTrigger>
        </TabsList>

        {loading ? (
          <div className="text-center py-12 text-gray-400">
            <p>加载中...</p>
          </div>
        ) : (
          <>
            <TabsContent value="bidding" className="space-y-4">
              {data.bidding.length > 0 ? (
                data.bidding.map((item) => (
                  <div key={item.id}>
                    {renderAnnouncementItem(item, 1)}
                  </div>
                ))
              ) : renderEmptyState()}
            </TabsContent>

            <TabsContent value="changes" className="space-y-4">
              {data.changes.length > 0 ? (
                data.changes.map((item) => (
                  <div key={item.id}>
                    {renderAnnouncementItem(item, 2)}
                  </div>
                ))
              ) : renderEmptyState()}
            </TabsContent>

            <TabsContent value="results" className="space-y-4">
              {data.results.length > 0 ? (
                data.results.map((item) => (
                  <div key={item.id}>
                    {renderAnnouncementItem(item, 3)}
                  </div>
                ))
              ) : renderEmptyState()}
            </TabsContent>

            <TabsContent value="others" className="space-y-4">
              {data.others.length > 0 ? (
                data.others.map((item) => (
                  <div key={item.id}>
                    {renderAnnouncementItem(item, 4)}
                  </div>
                ))
              ) : renderEmptyState()}
            </TabsContent>
          </>
        )}
      </Tabs>
    </div>
  );
}
